import {inject, injectable} from "inversify";
import {DivisionPromotion, LeagueDivision} from "@/modules/competitions/entity";
import {Season} from "@/modules/season/entity";
import {Team} from "@/modules/team/entity";
import LeagueDivisionMatchGenerator from "@/modules/competitions/services/divisions/LeagueDivisionMatchGenerator";
import {LeagueCompetition} from "@/modules/competitions/entity/LeagueCompetition";
import {ColorEnum} from "@/modules/vue/enums";
import {PromotionIconEnum} from "@/modules/competitions/enums";
import DivisionTableStatisticsGenerator
  from "@/modules/competitions/services/divisions/DivisionTableStatisticsGenerator";

@injectable()
export default class LeagueCompetitionFactory {
  constructor(
    @inject(LeagueDivisionMatchGenerator) private leagueDivisionMatchGenerator: LeagueDivisionMatchGenerator,
    @inject(DivisionTableStatisticsGenerator) private divisionTableStatisticsGenerator: DivisionTableStatisticsGenerator,
  ) {}

  async generate(
    season: Season,
    name: string,
    leagueTeams: Team[][],
    config: { relegateAndPromote: number, basePrice: number } = {relegateAndPromote: 3, basePrice: 5000}
  ): Promise<LeagueCompetition> {

    const competition = new LeagueCompetition();
    competition.name = name;
    await competition.save();
    console.debug(`generated competition: ${name}.`);

    let nextDivision: LeagueDivision;

    await leagueTeams.forEachAsync(async (teams: Team[], index: number) => {
      const division = new LeagueDivision();
      division.competition = competition;
      division.name = `${index + 1}. ${name}`;
      division.teams = teams;
      division.relegated = [teams[3]];
      division.promoted = [teams[1]];
      division.next = nextDivision;

      if (!competition.winner) {
        competition.winner = teams[0];
        await competition.save();
      }

      await division.save();

      if (nextDivision) {
        nextDivision.prev = division;
        await nextDivision.save();
      }

      await this.divisionTableStatisticsGenerator.generate(season, division);

      let remainRankFrom = 2;

      if (!nextDivision) {
        const relegation = new DivisionPromotion();
        relegation.division = division;
        relegation.icon = PromotionIconEnum.TROPHY;
        relegation.rank_form = 1;
        relegation.rank_to = 1;
        relegation.target_division = division;
        relegation.endOfTheSeason = true;
        relegation.colorInTable = ColorEnum.Lime;
        relegation.icon = PromotionIconEnum.TROPHY;
        relegation.price = config.basePrice;
        await relegation.save();

        if (teams.length > 4) {
          const r1 = new DivisionPromotion();
          r1.division = division;
          r1.rank_form = 2;
          r1.rank_to = 4;
          r1.target_division = division;
          r1.endOfTheSeason = true;
          r1.colorInTable = ColorEnum.Amber;
          r1.price = config.basePrice * .9;
          r1.icon = PromotionIconEnum.CUP;
          r1.showIconInTable = false;
          await r1.save();

          remainRankFrom = 5;
        }

        if (teams.length > 6) {
          const r2 = new DivisionPromotion();
          r2.division = division;
          r2.rank_form = 5;
          r2.rank_to = 6;
          r2.target_division = division;
          r2.endOfTheSeason = true;
          r2.colorInTable = ColorEnum.Blue;
          r2.price = config.basePrice * .8;
          r2.icon = PromotionIconEnum.CUP;
          r2.showIconInTable = false;
          await r2.save();

          remainRankFrom = 7;
        }
      }

      if (nextDivision) {
        const relegation = new DivisionPromotion();
        relegation.division = nextDivision;
        relegation.rank_form = teams.length - config.relegateAndPromote + 1;
        relegation.rank_to = teams.length;
        relegation.target_division = division;
        relegation.endOfTheSeason = true;
        relegation.colorInTable = ColorEnum.Red;
        relegation.icon = PromotionIconEnum.DOWN;
        relegation.price = config.basePrice * .5;
        await relegation.save();

        const promotion = new DivisionPromotion();
        promotion.division = division;
        promotion.rank_form = 1;
        promotion.rank_to = config.relegateAndPromote;
        promotion.target_division = nextDivision;
        promotion.endOfTheSeason = true;
        promotion.colorInTable = ColorEnum.Green;
        promotion.icon = PromotionIconEnum.UP;
        promotion.price = Math.round(config.basePrice / (index + 1));
        await promotion.save();

        remainRankFrom = config.relegateAndPromote + 1;
      }

      const remainPromotion = new DivisionPromotion();
      remainPromotion.division = division;
      remainPromotion.rank_form = remainRankFrom;
      remainPromotion.rank_to = index === leagueTeams.length - 1 ? teams.length : teams.length - config.relegateAndPromote;
      remainPromotion.target_division = division;
      remainPromotion.endOfTheSeason = true;
      remainPromotion.price = Math.round(config.basePrice / (index + 1.5));
      remainPromotion.icon = PromotionIconEnum.REMAIN;
      remainPromotion.showIconInTable = false;
      await remainPromotion.save();

      await this.leagueDivisionMatchGenerator.generate(season, division);

      nextDivision = division;

      console.debug(`created league division: ${division.name}.`);
    });

    return competition;
  }
}
